import { Badge, Box, Button, Flex, IconButton, Text, useColorMode } from '@chakra-ui/react';
import LanguageSelector from 'components/LanguageSelector';
import { Bell, Menu as MenuIcon, Moon, PanelLeft, Sun, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

interface TopbarProps {
  title?: string;
  crumb?: string;
  unread?: number;
  onOpenNotifications: () => void;
  onOpenMobileNav: () => void;
  /** kept for API compatibility; the user menu now lives in the sidebar footer */
  onOpenCompany?: () => void;
}

const Topbar = ({
  title,
  crumb,
  unread = 0,
  onOpenNotifications,
  onOpenMobileNav,
}: TopbarProps) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const isUpload = pathname === '/upload';
  // Chat / Summary / Translator keep their conversation-list / file-select panel in a mobile
  // drawer that opens on the 'open-sidebar-mobile' event; expose a trigger for it on phones so
  // that panel is reachable (it had no trigger before). (#78848, #78849)
  const isToolPage = pathname === '/' || pathname === '/summary' || pathname === '/translation';

  return (
    <Flex
      as='header'
      align='center'
      flexShrink={0}
      gap={3}
      h='topbar'
      px={{ base: 4, md: 6 }}
      bg='bg.surface'
      borderColor='border.default'
      borderBottomWidth='1px'
    >
      <IconButton
        display={{ base: 'flex', lg: 'none' }}
        aria-label='Open navigation'
        icon={<MenuIcon size={20} />}
        onClick={onOpenMobileNav}
        size='ds-sm'
        variant='ds-ghost'
      />

      {/* Mobile: open the tool's conversation-list / file-select drawer (Chat/Summary/Translator).
          The containers listen for this event; there was no trigger on phones. (#78848, #78849) */}
      {isToolPage && (
        <IconButton
          display={{ base: 'flex', lg: 'none' }}
          aria-label='Open list'
          icon={<PanelLeft size={20} />}
          onClick={() => window.dispatchEvent(new CustomEvent('open-sidebar-mobile'))}
          size='ds-sm'
          variant='ds-ghost'
        />
      )}

      <Flex direction='column' minW={0}>
        {crumb && (
          <Text color='text.subtle' fontSize='xs' fontWeight={500} noOfLines={1}>
            {crumb}
          </Text>
        )}
        {title && (
          <Text
            color='text.default'
            fontFamily='heading'
            fontSize='md'
            fontWeight={600}
            letterSpacing='-0.01em'
            noOfLines={1}
          >
            {title}
          </Text>
        )}
      </Flex>

      <Flex align='center' gap={2} ml='auto'>
        {/* Language — visible on mobile too (#77945 ②: was hidden < md) */}
        <Box>
          <LanguageSelector />
        </Box>

        {/* Theme toggle — icon shows the mode you'll switch TO (dark→Sun, light→Moon). #77529r2-9 */}
        <IconButton
          aria-label={colorMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          icon={colorMode === 'dark' ? <Sun size={19} /> : <Moon size={19} />}
          onClick={toggleColorMode}
          size='ds-md'
          variant='ds-ghost'
        />

        {/* Notifications */}
        <Box pos='relative'>
          <IconButton
            aria-label='Notifications'
            icon={<Bell size={19} />}
            onClick={onOpenNotifications}
            size='ds-md'
            variant='ds-ghost'
          />
          {unread > 0 && (
            <Badge
              pos='absolute'
              zIndex={1}
              top='-2px'
              right='-2px'
              alignItems='center'
              justifyContent='center'
              display='flex'
              minW='16px'
              h='16px'
              px={1}
              color='#fff'
              fontFamily='mono'
              fontSize='10px'
              fontWeight={700}
              bg='state.error'
              border='2px solid'
              borderColor='bg.surface'
              borderRadius='full'
            >
              {unread > 99 ? '99+' : unread}
            </Badge>
          )}
        </Box>

        {/* Divider separates the utility cluster from the primary Upload CTA */}
        <Box flexShrink={0} h='24px' mx={1} borderColor='border.default' borderLeftWidth='1px' />

        {/* Prominent Upload CTA — uploading is the first step before using the tools.
            Mobile: balanced square icon-only button; sm+: labelled. (#77945 ①) */}
        <IconButton
          sx={isUpload ? { filter: 'brightness(0.94)' } : undefined}
          display={{ base: 'flex', sm: 'none' }}
          shadow='ds_sm'
          _hover={{ shadow: 'ds_md' }}
          aria-current={isUpload ? 'page' : undefined}
          aria-label={t('route.upload')}
          icon={<Upload size={18} />}
          onClick={() => history.push('/upload')}
          size='ds-md'
          variant='ds-primary'
        />
        <Button
          sx={isUpload ? { filter: 'brightness(0.94)' } : undefined}
          display={{ base: 'none', sm: 'inline-flex' }}
          shadow='ds_sm'
          _hover={{ shadow: 'ds_md' }}
          aria-current={isUpload ? 'page' : undefined}
          aria-label={t('route.upload')}
          leftIcon={<Upload size={16} />}
          onClick={() => history.push('/upload')}
          size='ds-md'
          variant='ds-primary'
        >
          {t('route.upload')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default Topbar;
